import { useRef, useEffect, useState, useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import Avatar from '@cloudscape-design/chat-components/avatar'
import ChatBubble from '@cloudscape-design/chat-components/chat-bubble'
import Box from '@cloudscape-design/components/box'
import Container from '@cloudscape-design/components/container'
import ExpandableSection from '@cloudscape-design/components/expandable-section'
import Header from '@cloudscape-design/components/header'
import PromptInput from '@cloudscape-design/components/prompt-input'
import SpaceBetween from '@cloudscape-design/components/space-between'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import type { AgentStep, ChatMessage } from '@/hooks/useAgentChat'
import styles from './ChatPanel.module.css'

interface Props {
  messages: ChatMessage[]
  sendMessage: (text: string) => Promise<void>
  loading: boolean
  historyLoading: boolean
  onSent?: () => void
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

const TOOL_LABELS: Record<string, string> = {
  get_queue_counts: 'Checked queue counts',
  get_queue_items: 'Fetched queue items',
  get_prospect_counts: 'Checked prospect counts',
  get_prospects: 'Fetched prospects',
  get_recent_runs: 'Checked recent runs',
  approve_queue_item: 'Approved item',
  reject_queue_item: 'Rejected item',
}

function AgentStepsFooter({ steps }: { steps: AgentStep[] }) {
  const headerText = steps.length === 1 ? '1 tool call' : `${steps.length} tool calls`
  return (
    <ExpandableSection variant="footer" headerText={headerText}>
      <SpaceBetween size="xxs">
        {steps.map((step, i) => (
          <Box key={i} variant="small" color="text-body-secondary">
            <StatusIndicator type="info">{TOOL_LABELS[step.tool] ?? step.tool}</StatusIndicator>
          </Box>
        ))}
      </SpaceBetween>
    </ExpandableSection>
  )
}

function formatDayLabel(isoDay: string): string {
  const today = new Date().toISOString().slice(0, 10)
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10)
  if (isoDay === today) return 'Today'
  if (isoDay === yesterday) return 'Yesterday'
  return new Date(isoDay).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function ChatPanel({ messages, sendMessage, loading, historyLoading, onSent }: Props) {
  const [draft, setDraft] = useState('')
  const scrollAnchorRef = useRef<HTMLDivElement>(null)

  const groupedMessages = useMemo(() => {
    const groups: { day: string; label: string; msgs: ChatMessage[] }[] = []
    for (const msg of messages) {
      const day = msg.timestamp.slice(0, 10)
      const last = groups[groups.length - 1]
      if (last?.day === day) {
        last.msgs.push(msg)
      } else {
        groups.push({ day, label: formatDayLabel(day), msgs: [msg] })
      }
    }
    return groups
  }, [messages])

  useEffect(() => {
    if (messages.length > 0 || loading) {
      scrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, loading])

  function handleSend() {
    if (!draft.trim() || loading) return
    void sendMessage(draft)
    setDraft('')
    onSent?.()
  }

  return (
    <Container header={<Header variant="h2">Chat with your agent</Header>}>
      <SpaceBetween size="m">
        <div className={styles.messageList}>
          {historyLoading ? (
            <Box color="text-body-secondary" textAlign="center" padding="xl">
              <StatusIndicator type="loading">Loading conversation…</StatusIndicator>
            </Box>
          ) : messages.length === 0 && !loading ? (
            <Box color="text-body-secondary" textAlign="center" padding="xl">
              Ask your agent anything about your marketing performance.
            </Box>
          ) : (
            <SpaceBetween size="m">
              {groupedMessages.map((group) => (
                <SpaceBetween key={group.day} size="s">
                  <div className={styles.dateSeparator}>
                    <div className={styles.dateLine} />
                    <Box variant="small" color="text-body-secondary">{group.label}</Box>
                    <div className={styles.dateLine} />
                  </div>

                  {group.msgs.map((msg, i) =>
                    msg.role === 'agent' ? (
                      <ChatBubble
                        key={i}
                        type="incoming"
                        ariaLabel={`Agent at ${formatTime(msg.timestamp)}`}
                        avatar={
                          <Avatar
                            color="gen-ai"
                            iconName="gen-ai"
                            ariaLabel="Agent avatar"
                            tooltipText="Agent"
                          />
                        }
                      >
                        <div className={styles.markdownContent}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                        </div>
                        <Box variant="small" color="text-body-secondary">{formatTime(msg.timestamp)}</Box>
                        {msg.steps && msg.steps.length > 0 && <AgentStepsFooter steps={msg.steps} />}
                      </ChatBubble>
                    ) : (
                      <ChatBubble
                        key={i}
                        type="outgoing"
                        ariaLabel={`You at ${formatTime(msg.timestamp)}`}
                        avatar={
                          <Avatar
                            iconName="user-profile"
                            ariaLabel="Your avatar"
                            tooltipText="You"
                          />
                        }
                      >
                        <Box>{msg.content}</Box>
                        <Box variant="small" color="text-body-secondary">{formatTime(msg.timestamp)}</Box>
                      </ChatBubble>
                    )
                  )}
                </SpaceBetween>
              ))}

              {loading && (
                <ChatBubble
                  type="incoming"
                  showLoadingBar
                  ariaLabel="Agent is thinking"
                  avatar={
                    <Avatar
                      color="gen-ai"
                      iconName="gen-ai"
                      ariaLabel="Agent avatar"
                      loading
                    />
                  }
                >
                  <StatusIndicator type="loading">Thinking…</StatusIndicator>
                </ChatBubble>
              )}
            </SpaceBetween>
          )}
          <div ref={scrollAnchorRef} />
        </div>

        <PromptInput
          value={draft}
          onChange={({ detail }) => setDraft(detail.value)}
          onAction={handleSend}
          placeholder="Ask your agent..."
          disabled={loading || historyLoading}
          disableActionButton={!draft.trim() || historyLoading || loading}
          actionButtonIconName="send"
          minRows={1}
          maxRows={6}
          i18nStrings={{ actionButtonAriaLabel: 'Send message' }}
        />
      </SpaceBetween>
    </Container>
  )
}
